import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

const useSparklineData = (points = []) => ({
  labels: points.map((_, i) => String(i + 1)),
  datasets: [
    {
      data: points,
      borderColor: "#137fec",
      backgroundColor: "rgba(19,127,236,0.15)",
      tension: 0.35,
      pointRadius: 0,
      fill: true,
    },
  ],
});

const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
  },
  scales: {
    x: { display: false },
    y: { display: false },
  },
};

const chartBoxStyle = { height: 160 };

export default function AdminDashboardCharts({ attendanceData = [], vendorRequirements = [], stats }) {
  const labels = useMemo(() => {
    const out = [];
    const today = new Date();
    for (let i = 6; i >= 0; i -= 1) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      out.push(d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }));
    }
    return out;
  }, []);

  const attendanceTrend = useMemo(() => {
    const counts = labels.map(() => 0);
    attendanceData.forEach((a) => {
      const d = new Date(a.punchAt || a.date || a.timestamp || a.createdAt || Date.now());
      const key = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
      const idx = labels.indexOf(key);
      if (idx >= 0) counts[idx] += 1;
    });
    return counts;
  }, [attendanceData, labels]);

  const vendorTrend = useMemo(() => {
    const counts = labels.map(() => 0);
    vendorRequirements.forEach((r) => {
      const d = new Date(r.createdAt || Date.now());
      const key = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
      const idx = labels.indexOf(key);
      if (idx >= 0) counts[idx] += 1;
    });
    return counts;
  }, [vendorRequirements, labels]);

  const attendanceLine = {
    labels,
    datasets: [
      {
        label: "Attendance",
        data: attendanceTrend,
        borderColor: "#137fec",
        backgroundColor: "rgba(19,127,236,0.2)",
        fill: true,
        tension: 0.35,
        pointRadius: 2,
      },
    ],
  };

  const vendorBar = {
    labels,
    datasets: [
      {
        label: "Requirements",
        data: vendorTrend,
        backgroundColor: "rgba(16, 185, 129, 0.7)",
        borderRadius: 6,
      },
    ],
  };

  const statusCounts = useMemo(() => {
    let present = 0;
    let half = 0;
    let absent = 0;
    (attendanceData || []).forEach((a) => {
      const s = String(a.status || "").toLowerCase();
      if (s.includes("present")) present += 1;
      else if (s.includes("half")) half += 1;
      else absent += 1;
    });
    return { present, half, absent };
  }, [attendanceData]);

  const donut = {
    labels: ["Present", "Half Day", "Absent"],
    datasets: [
      {
        data: [statusCounts.present, statusCounts.half, statusCounts.absent],
        backgroundColor: ["#22c55e", "#f59e0b", "#ef4444"],
        borderWidth: 0,
      },
    ],
  };

  const sparkline = useSparklineData(attendanceTrend.slice(-8));
  const attendanceRate = stats?.totalEmployees
    ? Math.round(((stats?.presentCount || 0) / stats.totalEmployees) * 100)
    : 0;

  return (
    <div className="adash-grid charts">
      <div className="adash-card">
        <div className="adash-card-header">
          <div>
            <div className="adash-card-title">Attendance Trend</div>
            <div className="adash-subtitle">7-day movement from live attendance feed</div>
          </div>
          <div className="adash-chart-chip">{attendanceRate}% rate</div>
        </div>
        <div className="adash-card-body" style={chartBoxStyle}>
          <Line data={attendanceLine} options={{ ...baseOptions, scales: { x: { display: true }, y: { display: true } } }} />
        </div>
      </div>

      <div className="adash-card">
        <div className="adash-card-header">
          <div>
            <div className="adash-card-title">Requirements Inflow</div>
            <div className="adash-subtitle">New vendor requests over the last 7 days</div>
          </div>
          <div className="adash-chart-chip">{vendorRequirements.length} total</div>
        </div>
        <div className="adash-card-body" style={chartBoxStyle}>
          <Bar data={vendorBar} options={{ ...baseOptions, scales: { x: { display: true }, y: { display: true } } }} />
        </div>
      </div>

      <div className="adash-card">
        <div className="adash-card-header">
          <div>
            <div className="adash-card-title">Attendance Split</div>
            <div className="adash-subtitle">Present vs half-day vs absent breakdown</div>
          </div>
          <div className="adash-chart-chip">Today</div>
        </div>
        <div className="adash-card-body" style={{ height: 160 }}>
          <Doughnut data={donut} options={{ plugins: { legend: { position: "bottom" } } }} />
        </div>
      </div>

      <div className="adash-card">
        <div className="adash-card-header">
          <div>
            <div className="adash-card-title">Live Pulse</div>
            <div className="adash-subtitle">Compressed pulse from recent attendance activity</div>
          </div>
          <div className="adash-chart-chip">Live</div>
        </div>
        <div className="adash-card-body" style={{ height: 160 }}>
          <Line data={sparkline} options={baseOptions} />
        </div>
      </div>
    </div>
  );
}
